import { useMemo, useState, type ReactNode } from 'react'
import { Users, ListChecks, Trophy } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import {
  useDependientesAnalyticsDifare,
  useFasesAnalyticsDifare,
  useVisitadoresAnalyticsDifare,
} from '../hooks/useAnalyticsDifare'
import { KpiCard, INDIGO, VERDE, AZUL } from './KpiCard'
import { nf, toTitleCase, fechaCorta } from '../format'
import type { DependienteFila } from '../types'

const ALL = 'all'

type SortKey =
  | 'nombre'
  | 'punto_venta'
  | 'visitador'
  | 'puntos_f1'
  | 'puntos_f2'
  | 'puntos_f3'
  | 'total'
  | 'puntos_fase'

const TEXT_KEYS: SortKey[] = ['nombre', 'punto_venta', 'visitador']

export function DependientesTab() {
  const [fase, setFase] = useState<number | undefined>(undefined)
  const [visitadorId, setVisitadorId] = useState<number | undefined>(undefined)
  const [codigoPV, setCodigoPV] = useState<string | undefined>(undefined)
  // Orden por defecto: total (mayor a menor). En vista por fase se ordena por
  // puntos_fase (ver mapEff). Clic en un encabezado numérico reordena.
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'total',
    dir: 'desc',
  })
  const [busqueda, setBusqueda] = useState('')

  const filtro = { fase, visitador_id: visitadorId, codigo_punto_venta: codigoPV }
  const { data, isLoading, error } = useDependientesAnalyticsDifare(filtro)
  const { data: fases } = useFasesAnalyticsDifare()
  const { data: visitadores } = useVisitadoresAnalyticsDifare()
  // Query sin filtros: fuente de opciones de punto de venta (react-query la cachea).
  const { data: todos } = useDependientesAnalyticsDifare({})

  const pvOptions = useMemo(() => {
    const set = new Set<string>()
    ;(todos?.filas ?? []).forEach((f) => {
      if (f.punto_venta) set.add(f.punto_venta)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'))
  }, [todos])

  const faseActiva = fase !== undefined

  // En vista por fase la única columna numérica es "Puntos" (puntos_fase):
  // cualquier orden numérico se mapea a ese campo.
  const mapEff = (k: SortKey): SortKey =>
    faseActiva && !TEXT_KEYS.includes(k) ? 'puntos_fase' : k
  const effKey = mapEff(sort.key)

  const toggleSort = (k: SortKey) => {
    setSort((prev) =>
      mapEff(prev.key) === mapEff(k)
        ? { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: k, dir: TEXT_KEYS.includes(k) ? 'asc' : 'desc' },
    )
  }

  const filasOrdenadas = useMemo(() => {
    const filas = data?.filas ?? []
    const q = busqueda.trim().toLowerCase()
    const filtradas = q
      ? filas.filter(
          (f) =>
            `${f.nombre} ${f.apellido}`.toLowerCase().includes(q) ||
            (f.punto_venta ?? '').toLowerCase().includes(q) ||
            (f.visitador ?? '').toLowerCase().includes(q),
        )
      : filas
    const val = (f: DependienteFila): string | number => {
      switch (effKey) {
        case 'nombre':
          return `${f.nombre} ${f.apellido}`.trim().toLowerCase()
        case 'punto_venta':
          return f.punto_venta ?? ''
        case 'visitador':
          return (f.visitador ?? '').toLowerCase()
        case 'puntos_fase':
          return f.puntos_fase ?? 0
        default:
          return (f[effKey] as number) ?? 0
      }
    }
    return [...filtradas].sort((a, b) => {
      const va = val(a)
      const vb = val(b)
      if (typeof va === 'string' || typeof vb === 'string') {
        const c = String(va).localeCompare(String(vb), 'es')
        return sort.dir === 'asc' ? c : -c
      }
      return sort.dir === 'asc' ? va - vb : vb - va
    })
  }, [data, effKey, sort.dir, busqueda])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-destructive text-sm text-center max-w-md">
          No se pudo cargar el reporte de dependientes. Verifique la configuración e intente de nuevo.
        </p>
      </div>
    )
  }

  const serie = (data.serie ?? []).map((p) => ({
    fecha: fechaCorta(p.fecha),
    cantidad: p.cantidad,
  }))

  const th = (k: SortKey, label: string, align: 'left' | 'right' = 'left'): ReactNode => {
    const active = mapEff(k) === effKey
    return (
      <TableHead
        className={`cursor-pointer select-none ${align === 'right' ? 'text-right' : ''}`}
        onClick={() => toggleSort(k)}
      >
        <span
          className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}
        >
          {label}
          <span className="text-[10px] leading-none text-muted-foreground">
            {active ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
          </span>
        </span>
      </TableHead>
    )
  }

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Fase</span>
          <Select
            value={fase === undefined ? ALL : String(fase)}
            onValueChange={(v) => setFase(v === ALL ? undefined : Number(v))}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas</SelectItem>
              {(fases ?? []).map((f) => (
                <SelectItem key={f.numero} value={String(f.numero)}>
                  {f.nombre} ({fechaCorta(f.fecha_inicio)}–{fechaCorta(f.fecha_fin)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Visitador</span>
          <Select
            value={visitadorId === undefined ? ALL : String(visitadorId)}
            onValueChange={(v) => setVisitadorId(v === ALL ? undefined : Number(v))}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              {(visitadores ?? []).map((v) => (
                <SelectItem key={v.visitador_id} value={String(v.visitador_id)}>
                  {toTitleCase(v.nombre)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Punto de venta</span>
          <Select
            value={codigoPV ?? ALL}
            onValueChange={(v) => setCodigoPV(v === ALL ? undefined : v)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              {pvOptions.map((pv) => (
                <SelectItem key={pv} value={pv}>
                  {pv}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={Users}
          label="Participantes"
          value={nf(data.cards.participantes)}
          sub="Con ≥1 trivia en el alcance"
          color={AZUL}
        />
        <KpiCard
          icon={ListChecks}
          label="Completaron"
          value={nf(data.cards.completaron)}
          sub="Trivia completa (3/3)"
          color={VERDE}
        />
        <KpiCard
          icon={Trophy}
          label="Puntaje promedio"
          value={data.cards.puntaje_promedio.toFixed(1)}
          sub="Entre participantes"
          color={INDIGO}
        />
      </div>

      {/* Trivias por fecha */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Trivias por fecha</CardTitle>
        </CardHeader>
        <CardContent>
          {serie.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No hay trivias en el alcance seleccionado.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={serie} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => [nf(v), 'Trivias']} />
                <Bar dataKey="cantidad" fill={INDIGO} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabla por dependiente */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-sm font-medium">
            Dependientes ({nf(filasOrdenadas.length)})
          </CardTitle>
          <Input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, PV o representante…"
            className="h-9 w-full sm:w-72"
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]">
                  {th('nombre', 'Dependiente')}
                  {th('punto_venta', 'PV')}
                  {th('visitador', 'Representante')}
                  {faseActiva ? (
                    th('total', 'Puntos', 'right')
                  ) : (
                    <>
                      {th('puntos_f1', 'F1', 'right')}
                      {th('puntos_f2', 'F2', 'right')}
                      {th('puntos_f3', 'F3', 'right')}
                      {th('total', 'Total', 'right')}
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filasOrdenadas.length > 0 ? (
                  filasOrdenadas.map((f) => (
                    <TableRow key={f.dependiente_id}>
                      <TableCell className="font-medium">
                        {toTitleCase(`${f.nombre} ${f.apellido}`.trim()) || '—'}
                      </TableCell>
                      <TableCell>{f.punto_venta || '—'}</TableCell>
                      <TableCell>{toTitleCase(f.visitador) || '—'}</TableCell>
                      {faseActiva ? (
                        <TableCell className="text-right tabular-nums">
                          {nf(f.puntos_fase ?? 0)}
                        </TableCell>
                      ) : (
                        <>
                          <TableCell className="text-right tabular-nums">{nf(f.puntos_f1)}</TableCell>
                          <TableCell className="text-right tabular-nums">{nf(f.puntos_f2)}</TableCell>
                          <TableCell className="text-right tabular-nums">{nf(f.puntos_f3)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">
                            {nf(f.total)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={faseActiva ? 4 : 7} className="text-center text-muted-foreground py-10">
                      Sin dependientes para el filtro seleccionado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
